import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-[var(--radius)] text-sm font-normal transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        glow: "bg-primary text-primary-foreground relative overflow-hidden",
        destructive:
          "border border-input/50 bg-destructive/10 text-destructive hover:bg-destructive hover:text-white dark:text-foreground dark:hover:bg-destructive",
        outline:
          "border border-input bg-background text-foreground shadow-sm hover:bg-accent/10 hover:text-accent-foreground dark:bg-zinc-800 dark:border-input/50 dark:hover:bg-input/80",
        secondary:
          "border border-input/50 bg-secondary text-secondary-foreground hover:bg-secondary/90",
        ghost:
          "text-foreground hover:bg-accent/5 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80",
        subtle:
          "bg-primary/20 text-primary font-semibold hover:bg-primary/30 dark:bg-primary/15 dark:text-primary-100/80",
        success:
          "bg-green-100 text-green-800 hover:bg-green-600 hover:text-white dark:bg-green-600/40 dark:text-green-200 dark:hover:bg-green-600/80",
        warning:
          "bg-yellow-100 text-yellow-800 hover:bg-yellow-600 hover:text-white dark:bg-yellow-600/40 dark:text-yellow-200 dark:hover:bg-yellow-600/80",
        info: "bg-blue-600/80 text-primary-foreground hover:bg-blue-600 dark:bg-blue-600/40 hover:text-white",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3 text-sm",
        sm: "h-8 px-2.5 text-xs",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltip?: string;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  tooltipAlign?: "start" | "center" | "end";
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  tooltip,
  tooltipSide = "top",
  tooltipAlign = "center",
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  const buttonContent = (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {children}
    </Comp>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
          <TooltipContent side={tooltipSide} align={tooltipAlign}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonContent;
}

export { Button, buttonVariants };
